import { useState, useEffect } from "react";
import { Loader2, Plus, Trash2, Crown, Mail, X } from "lucide-react";
import { api } from "../lib/api";
import { DEFAULT_CURRENCY } from "../lib/defaultCurrency";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { Dialog, DialogFooter } from "./EditPercentagesDialog";
import { MemberColorPicker } from "./MemberColorPicker";
import { defaultMemberColor } from "../lib/memberColors";

function newMemberRow(index = 0) {
    return {
        client_id: crypto.randomUUID(),
        name: "",
        role_label: "",
        email: "",
        percentage: "",
        color: defaultMemberColor(index),
        invite_user: false,
        pool_role: "recipient",
    };
}

function defaultMemberRoleLabel(member, isSelf) {
    const custom = (member.role_label || "").trim();
    if (custom) return custom;
    if (isSelf) return "Owner";
    if (member.pool_role === "co_owner") return "Co-owner";
    return "Member";
}

function validateCreatePoolForm({ name, selfMemberId, members, validTotal, total }) {
    if (!name.trim()) {
        return "Enter a pool name";
    }
    if (!selfMemberId) {
        return "Select which member row represents you before creating the pool";
    }
    for (let i = 0; i < members.length; i += 1) {
        const member = members[i];
        const rowLabel = `Member ${i + 1}`;
        if (!(member.name || "").trim()) {
            return `Enter a name for ${rowLabel}`;
        }
        if (member.percentage === "" || member.percentage == null || Number.isNaN(parseFloat(member.percentage))) {
            return `Enter a share percentage for ${member.name.trim()}`;
        }
    }
    if (!validTotal) {
        return `Member percentages must sum to 100% (currently ${total.toFixed(2)}%)`;
    }
    return null;
}

export function CreatePoolDialog({ open, onClose, onCreated }) {
    const { activeOrg, user } = useAuth();
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [cadence, setCadence] = useState("weekly");
    const [currency, setCurrency] = useState(DEFAULT_CURRENCY);
    const [members, setMembers] = useState([newMemberRow()]);
    const [selfMemberId, setSelfMemberId] = useState(null);
    const [busy, setBusy] = useState(false);
    const [memberLimit, setMemberLimit] = useState(null);

    useEffect(() => {
        if (!open || !activeOrg?.id) return;
        let mounted = true;
        api.get("/billing/subscription", { params: { org_id: activeOrg.id } })
            .then(({ data }) => {
                if (!mounted) return;
                const limit = data?.limits?.max_pool_members ?? null;
                const used = data?.usage?.pool_members ?? 0;
                setMemberLimit(limit == null ? null : Math.max(0, limit - used));
            })
            .catch(() => {
                if (mounted) setMemberLimit(null);
            });
        return () => { mounted = false; };
    }, [open, activeOrg?.id]);

    const total = members.reduce((s, m) => s + (parseFloat(m.percentage) || 0), 0);
    const validTotal = Math.abs(total - 100) < 0.01;
    const allMembersComplete = members.every((m) => (m.name || "").trim() && m.percentage !== "" && m.percentage != null);
    const atMemberLimit = memberLimit != null && members.length >= memberLimit;
    const canSubmit = Boolean(name.trim()) && Boolean(selfMemberId) && allMembersComplete && validTotal;
    const limitHint = memberLimit == null
        ? null
        : `${members.length}/${memberLimit} pool member${memberLimit === 1 ? "" : "s"} for this plan`;

    if (!open) return null;

    const addRow = () => {
        if (atMemberLimit) {
            toast.error("Free plan limit reached", { description: "Upgrade to add more pool members." });
            return;
        }
        setMembers((rows) => [...rows, newMemberRow(rows.length)]);
    };
    const removeRow = (clientId) => {
        setMembers((rows) => rows.filter((r) => r.client_id !== clientId));
        if (selfMemberId === clientId) setSelfMemberId(null);
    };
    const updateRow = (clientId, key, val) => {
        setMembers((rows) => rows.map((r) => (r.client_id === clientId ? { ...r, [key]: val } : r)));
    };
    const selectSelf = (clientId) => {
        setSelfMemberId(clientId);
        setMembers((rows) => rows.map((r) => (
            r.client_id === clientId
                ? { ...r, invite_user: false, pool_role: "recipient" }
                : r
        )));
    };

    const toggleInvite = (clientId) => {
        setMembers((rows) => rows.map((r) => {
            if (r.client_id !== clientId) return r;
            const nextInvite = !r.invite_user;
            return {
                ...r,
                invite_user: nextInvite,
                pool_role: nextInvite ? r.pool_role : "recipient",
            };
        }));
        if (selfMemberId === clientId) setSelfMemberId(null);
    };

    const toggleCoOwner = (clientId) => {
        setMembers((rows) => rows.map((r) => {
            if (r.client_id !== clientId) return r;
            if (!r.invite_user) return { ...r, invite_user: true, pool_role: "co_owner" };
            return { ...r, pool_role: r.pool_role === "co_owner" ? "recipient" : "co_owner" };
        }));
        if (selfMemberId === clientId) setSelfMemberId(null);
    };

    const submit = async (e) => {
        e.preventDefault();
        const validationError = validateCreatePoolForm({ name, selfMemberId, members, validTotal, total });
        if (validationError) {
            toast.error(validationError);
            return;
        }
        for (const m of members) {
            if (m.invite_user && !m.email) {
                toast.error(`Email is required to invite ${m.name.trim()}`);
                return;
            }
        }
        try {
            setBusy(true);
            const payload = {
                name: name.trim(),
                description,
                cadence,
                currency,
                owner_user_id: user.id,
                self_member_id: selfMemberId,
                members: members.map((m) => {
                    const isSelf = m.client_id === selfMemberId;
                    return {
                        client_id: m.client_id,
                        name: m.name.trim(),
                        email: isSelf ? (m.email || user.email) : (m.email || undefined),
                        role_label: defaultMemberRoleLabel(m, isSelf),
                        percentage: parseFloat(m.percentage),
                        color: m.color,
                        invite_user: Boolean(m.invite_user),
                        pool_role: m.pool_role === "co_owner" ? "co_owner" : "recipient",
                    };
                }),
            };
            const { data } = await api.post("/pools", payload, { params: { org_id: activeOrg.id } });
            const inviteResults = data.invitations || [];
            const sentCount = inviteResults.filter((r) => r.ok).length;
            const failedInvites = inviteResults.filter((r) => !r.ok);
            const intendedInvites = members.filter((m) => m.invite_user).length;
            toast.success("Pool created", {
                description: sentCount
                    ? `${data.pool.name} created · ${sentCount} invitation${sentCount === 1 ? "" : "s"} sent`
                    : intendedInvites && !inviteResults.length
                        ? `${data.pool.name} created · invitation delivery pending`
                        : data.pool.name,
            });
            if (failedInvites.length) {
                toast.error("Some invitations failed", {
                    description: failedInvites.map((r) => `${r.email}: ${r.error}`).join("; "),
                });
            }
            onCreated?.(data.pool);
        } catch (err) {
            toast.error(err.response?.data?.detail || err.message);
        } finally { setBusy(false); }
    };

    return (
        <Dialog onClose={onClose} title="Create a new pool" subtitle="Define how revenue should be split across a group." testid="create-pool-dialog" size="xl">
            <form onSubmit={submit} className="space-y-4" data-testid="create-pool-form">
                <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2">
                        <label className="label-eyebrow">Pool name</label>
                        <input value={name} onChange={(e) => setName(e.target.value)} required placeholder="Q1 Operations Pool" className="input-dark mt-1.5 w-full rounded-md px-3 py-2 text-sm" data-testid="create-pool-name" />
                    </div>
                    <div className="col-span-2">
                        <label className="label-eyebrow">Description (optional)</label>
                        <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What this pool is for…" className="input-dark mt-1.5 w-full rounded-md px-3 py-2 text-sm" data-testid="create-pool-description" />
                    </div>
                    <div>
                        <label className="label-eyebrow">Cadence</label>
                        <select value={cadence} onChange={(e) => setCadence(e.target.value)} className="input-dark mt-1.5 w-full rounded-md px-3 py-2 text-sm" data-testid="create-pool-cadence">
                            <option value="weekly">Weekly</option>
                            <option value="biweekly">Bi-weekly</option>
                            <option value="monthly">Monthly</option>
                        </select>
                    </div>
                    <div>
                        <label className="label-eyebrow">Currency</label>
                        <select value={currency} onChange={(e) => setCurrency(e.target.value)} className="input-dark mt-1.5 w-full rounded-md px-3 py-2 text-sm" data-testid="create-pool-currency">
                            <option value="GBP">GBP</option>
                            <option value="USD">USD</option>
                            <option value="EUR">EUR</option>
                            <option value="CAD">CAD</option>
                        </select>
                    </div>
                </div>

                <div>
                    <div className="flex items-center justify-between gap-3">
                        <div>
                            <label className="label-eyebrow">Members</label>
                            <p className="mt-1 text-xs text-white/50">Mark yourself, or invite anyone by email — they&apos;ll get a link to join (new users create an account).</p>
                            {limitHint && (
                                <p className="mt-1 text-xs text-white/40" data-testid="create-pool-member-limit-hint">{limitHint}</p>
                            )}
                        </div>
                        <button type="button" onClick={addRow} disabled={atMemberLimit} className="text-xs text-[#00BF0D] hover:text-white inline-flex items-center gap-1 shrink-0 disabled:opacity-40 disabled:hover:text-[#00BF0D]" data-testid="add-member-row"><Plus className="h-3 w-3" /> Add</button>
                    </div>
                    <div className="create-pool-members-list mt-3">
                        <div className="create-pool-members-header text-[10px] uppercase tracking-wide text-white/40">
                            <span className="create-pool-members-header__color">Color</span>
                            <span>Name</span>
                            <span>Role</span>
                            <span>Email</span>
                            <span>%</span>
                            <span className="create-pool-members-header__actions">You</span>
                            <span className="sr-only">Remove</span>
                        </div>
                        {members.map((m, idx) => {
                            const isSelf = selfMemberId === m.client_id;
                            return (
                            <div key={m.client_id} className="create-pool-member-block">
                            <div className="create-pool-member-row">
                                <div className="create-pool-member-row__color">
                                    <MemberColorPicker
                                        value={m.color}
                                        onChange={(color) => updateRow(m.client_id, "color", color)}
                                        testId={`member-color-${idx}`}
                                    />
                                </div>
                                <label className="create-pool-field create-pool-member-row__name">
                                    <span className="create-pool-field-label">Name</span>
                                    <input placeholder="Name" value={m.name} onChange={(e) => updateRow(m.client_id, "name", e.target.value)} className="input-dark w-full rounded-md px-2.5 py-2 text-xs min-w-0" data-testid={`member-name-${idx}`} />
                                </label>
                                <label className="create-pool-field create-pool-member-row__role">
                                    <span className="create-pool-field-label">Role</span>
                                    <input placeholder="Role (e.g. Founder)" value={m.role_label} onChange={(e) => updateRow(m.client_id, "role_label", e.target.value)} className="input-dark w-full rounded-md px-2.5 py-2 text-xs min-w-0" data-testid={`member-role-${idx}`} />
                                </label>
                                <label className="create-pool-field create-pool-member-row__email">
                                    <span className="create-pool-field-label">Email</span>
                                    <input
                                        placeholder={
                                            isSelf
                                                ? user?.email || "Your email"
                                                : m.invite_user
                                                    ? "Email (required for invite)"
                                                    : "Email (optional)"
                                        }
                                        value={m.email}
                                        onChange={(e) => updateRow(m.client_id, "email", e.target.value)}
                                        type="email"
                                        required={m.invite_user}
                                        className="input-dark w-full rounded-md px-2.5 py-2 text-xs min-w-0"
                                        data-testid={`member-email-${idx}`}
                                    />
                                </label>
                                <label className="create-pool-field create-pool-member-row__pct">
                                    <span className="create-pool-field-label">Share %</span>
                                    <input placeholder="%" value={m.percentage} onChange={(e) => updateRow(m.client_id, "percentage", e.target.value)} type="number" min="0" max="100" step="0.01" className="input-dark w-full rounded-md px-2 py-2 text-xs font-mono min-w-0" data-testid={`member-pct-${idx}`} />
                                </label>
                                <div className="create-pool-member-row__actions">
                                    <button
                                        type="button"
                                        onClick={() => selectSelf(m.client_id)}
                                        className={`create-pool-self-btn ${isSelf ? "is-selected" : ""}`}
                                        aria-pressed={isSelf}
                                        aria-label={`${isSelf ? "Selected as you" : "Mark as you"} (${m.name || `member ${idx + 1}`})`}
                                        title={isSelf ? "This is you" : "Mark as you"}
                                        data-testid={`member-self-${idx}`}
                                    >
                                        {isSelf ? "✓" : "Me"}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => toggleInvite(m.client_id)}
                                        disabled={isSelf}
                                        className={`create-pool-self-btn ${isSelf ? "is-blocked" : m.invite_user ? "is-selected" : ""}`}
                                        aria-pressed={!isSelf && m.invite_user}
                                        aria-disabled={isSelf}
                                        title={isSelf ? "Can't invite your own row" : "Send email invite"}
                                        data-testid={`member-invite-${idx}`}
                                    >
                                        {isSelf ? <X className="h-3.5 w-3.5" strokeWidth={2.5} /> : <Mail className="h-3.5 w-3.5" />}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => toggleCoOwner(m.client_id)}
                                        disabled={isSelf || !m.invite_user}
                                        className={`create-pool-self-btn ${
                                            isSelf
                                                ? "is-blocked"
                                                : m.pool_role === "co_owner"
                                                    ? "is-selected"
                                                    : !m.invite_user
                                                        ? "is-muted"
                                                        : ""
                                        }`}
                                        aria-pressed={!isSelf && m.pool_role === "co_owner"}
                                        aria-disabled={isSelf || !m.invite_user}
                                        title={
                                            isSelf
                                                ? "Can't make your own row a co-owner invite"
                                                : m.invite_user
                                                    ? "Invite as co-owner"
                                                    : "Enable invite first"
                                        }
                                        data-testid={`member-coowner-${idx}`}
                                    >
                                        {isSelf ? <X className="h-3.5 w-3.5" strokeWidth={2.5} /> : <Crown className="h-3.5 w-3.5" />}
                                    </button>
                                </div>
                                <button type="button" onClick={() => removeRow(m.client_id)} disabled={members.length <= 1} className="create-pool-member-row__remove inline-flex h-8 w-8 items-center justify-center justify-self-end rounded-md text-white/40 transition-colors hover:bg-rose-500/10 hover:text-rose-400 disabled:opacity-30 disabled:hover:bg-transparent" data-testid={`member-remove-${idx}`}>
                                    <Trash2 className="h-3.5 w-3.5" />
                                </button>
                            </div>
                            {m.invite_user && (
                                <p className="create-pool-member-hint text-[10px] text-amber-400/85">
                                    {m.pool_role === "co_owner" ? "Co-owner invite · awaiting acceptance" : "Pool invite · awaiting acceptance"}
                                </p>
                            )}
                            </div>
                            );
                        })}
                    </div>
                    {!selfMemberId && (
                        <p className="mt-2 text-xs text-amber-400/90" data-testid="self-member-required-hint">
                            Select which member row represents you before creating the pool.
                        </p>
                    )}
                    <div className={`mt-2 flex justify-between text-xs ${validTotal ? "text-[#00BF0D]" : "text-rose-400"}`}>
                        <span>Allocation total</span>
                        <span className="font-mono font-semibold">{total.toFixed(2)}%</span>
                    </div>
                </div>

                <DialogFooter>
                    <button type="button" onClick={onClose} className="btn-ghost rounded-md px-4 py-2 text-sm">Cancel</button>
                    <button type="submit" disabled={busy || !canSubmit} className="btn-neon rounded-md px-4 py-2 text-sm inline-flex items-center gap-2 disabled:opacity-50" data-testid="create-pool-submit">
                        {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                        {busy ? "Creating…" : "Create pool"}
                    </button>
                </DialogFooter>
            </form>
        </Dialog>
    );
}
